<template>
  <div>
    <div v-if="!($store.getters.experimentChipsFiltersVisible || $store.state.experimentChipsFiltersWasMounted)" class="v-search-page-filters">
      <SearchFilters />
    </div>
    <div
      v-if="rooms.length > 0"
      class="mx-4 my-4 sm:my-6"
    >
      <div class="w-full">
        <SearchResultsMetaHeader />

        <div v-if="$store.state.debugFilterMode">
          Showing
          {{ $store.getters.debugFilterModeRoomsDisplayed }}
          / {{ $store.getters.debugFilterModeRoomsDisplayedAll }} spaces
        </div>

        <div v-if="isChristmasParty && isChristmasSeason">
          <div class="bg-green-100 flex rounded-md p-4 mt-4">
            <svg
              class="shrink-0"
              width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M16.667 33.3334H23.3337V35.6667C23.3337 36.219 22.8859 36.6667 22.3337 36.6667H17.667C17.1147 36.6667 16.667 36.219 16.667 35.6667V33.3334Z" fill="#00A287" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M19.2003 2.73301L11.2003 13.3997C10.7058 14.0589 11.1762 14.9997 12.0003 14.9997H14.8151L9.58887 21.7191C9.07798 22.3759 9.54608 23.333 10.3782 23.333H12.7275L7.82195 30.0782C7.34125 30.7391 7.8134 31.6663 8.63069 31.6663H31.3698C32.1871 31.6663 32.6593 30.7391 32.1786 30.0782L27.273 23.333H29.6223C30.4544 23.333 30.9225 22.3759 30.4116 21.7191L25.1854 14.9997H28.0003C28.8243 14.9997 29.2947 14.0589 28.8003 13.3997L20.8003 2.73301C20.4003 2.19967 19.6003 2.19968 19.2003 2.73301ZM16.667 28.3333C17.5875 28.3333 18.3337 27.5871 18.3337 26.6666C18.3337 25.7461 17.5875 24.9999 16.667 24.9999C15.7466 24.9999 15.0004 25.7461 15.0004 26.6666C15.0004 27.5871 15.7466 28.3333 16.667 28.3333ZM25.0004 19.9999C25.0004 20.9204 24.2542 21.6666 23.3337 21.6666C22.4132 21.6666 21.667 20.9204 21.667 19.9999C21.667 19.0795 22.4132 18.3333 23.3337 18.3333C24.2542 18.3333 25.0004 19.0795 25.0004 19.9999ZM18.3337 13.3333C19.2542 13.3333 20.0004 12.5871 20.0004 11.6666C20.0004 10.7461 19.2542 9.99995 18.3337 9.99995C17.4132 9.99995 16.667 10.7461 16.667 11.6666C16.667 12.5871 17.4132 13.3333 18.3337 13.3333Z" fill="#00A287" />
            </svg>

            <div class="ml-2">
              <div class="font-bold">
                Planning a {{ $store.getters.christmasLabel }}?
              </div>
              Search for
              <a
                class="cursor-pointer font-bold italic"
                @click="christmasBannerClick"
              >
                {{ $store.getters.christmasLabel }} venues</a> instead to see better results.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-show="isMobile || !showAdvancedFilters"
      class="v-search-page-results"
      ref="searchResults"
    >
      <HighlightedRoomCard />

      <div class="v-search-results">
        <div
          v-show="roomsSlice.length > 0"
          ref="searchResultsItems"
          class="v-search-results-items"
          :class="{
            'v-search-results-items--loading': loading,
          }"
        >
          <!-- eslint-disable-next-line vue/no-v-for-template-key -->
          <template v-for="(room, i) in roomsSlice" :key="getRoomKey(room, i)">
            <ConciergeSearchCard
              v-if="isConciergeCardVisible && i === conciergeCardIndex"
              :brief-url="conciergeBriefUrl"
              :event-category="$store.state.eventCategory"
            />
            <RoomCard
              :room="room"
              :index="i"
              :manager-last-activity-online="$store.state.managersLastActivityOnline[room.venue_id]"
              :search-event-category="$store.state.eventCategory"
              @beforeSlide="roomEvents.beforeSlide($event, room, i)"
              @mouseOver="roomEvents.mouseOver(room)"
              @mouseOut="roomEvents.mouseOut(room)"
              @trackClick="roomEvents.trackClick($event, room, i)"
            />
          </template>
          <div
            v-for="i in 3"
            :key="i"
            class="c-room-card c-room-card--empty v-room-carousel--empty"
          />
        </div>
      </div>

      <RemovableFiltersResultsSection
        v-if="$store.state.roomsReducedFilters.length > 0"
        @roomCardMouseOver="roomEvents.mouseOver"
        @roomCardMouseOut="roomEvents.mouseOut"
      />

      <div v-if="rooms.length <= 6 && !loading && page === 1 && !loadingReducedFilters && $store.state.roomsReducedFilters.length === 0">
        <SearchEmptyState
          class="p-4 mt-4"
          :rooms-number="rooms.length"
        />
      </div>

      <div
        v-if="pageCount > 1 && !$store.state.debugFilterMode"
        class="results-pagination results-pagination--center"
        :key="page"
      >
        <BasePagination
          class="pb-30 im:pb-0"
          :model-value="page"
          :page-range="isMobile ? 3 : 5"
          position="center"
          :page-count="pageCount"
          @change="paginationChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import throttle from 'lodash/throttle';
import { mapActions, mapGetters, mapState } from 'vuex';

import BasePagination from '@/components/BasePagination.vue';
import ScreenWidth from '@/components/ScreenWidth';
import {
  getBoundariesCenter,
  setAreaPrefill,
  withPrefill,
} from '@/modules/public/shared/concierge/concierge';
import { HOURS_MAP_24 } from '@/services/hours-map';

import { isMobile as isMobileFn } from '../services.js';
import ConciergeSearchCard from './ConciergeSearchCard.vue';
import SearchEmptyState from './EmptyState/SearchEmptyState.vue';
import HighlightedRoomCard from './HighlightedRoomCard.vue';
import RemovableFiltersResultsSection from './RemovableFiltersResultsSection.vue';
import RoomCard from './RoomCard.vue';
import SearchFilters from './SearchFilters/SearchFilters.vue';
import SearchResultsMetaHeader from './SearchResultsMetaHeader.vue';

// Mirrors the widths at which RoomCard.vue lays the results out three to a row
const threeColumnGridQuery = window.matchMedia('(min-width: 1680px), (min-width: 901px) and (max-width: 991.98px)');

export default {
  name: 'SearchRooms',
  mixins: [ScreenWidth],
  components: {
    ConciergeSearchCard,
    SearchResultsMetaHeader,
    SearchEmptyState,
    RoomCard,
    SearchFilters,
    BasePagination,
    HighlightedRoomCard,
    RemovableFiltersResultsSection,
  },
  data() {
    return {
      christmasBannerSeen: false,
      concierge: window._VUE_SEARCH_CONFIG?.concierge || null,
      isGuest: false,
      isThreeColumnGrid: threeColumnGridQuery.matches,
    };
  },
  computed: {
    ...mapState([
      'loading',
      'loadingReducedFilters',
      'displayedRooms',
      'showAdvancedFilters',
      'mobileView',
      'rooms',
      'boundaries',
      'perPage',
      'page',
      'visibleRoomsNumber',
      'filters',
      'eventType',
      'eventTypeSlug',
      'metaHeader',
    ]),
    ...mapGetters([
      'roomsSlice',
      'roomsNumber',
      'nonVisibleRooms',
      'pageCount',
      'visibleRooms',
      'isChristmasParty',
      'isChristmasSeason',
      'selectedDateMonthLong',
    ]),
    roomEvents() {
      return {
        beforeSlide: this.beforeSlide,
        trackClick: this.trackClick,
        mouseOver: this.mouseOver,
        mouseOut: this.mouseOut,
      };
    },
    isChristmasBannerVisible() {
      return this.rooms.length > 0 && this.isChristmasParty && this.isChristmasSeason;
    },
    isConciergeCardVisible() {
      return Boolean(this.concierge) && this.isGuest && !this.concierge.excludedTags.includes(this.eventTypeSlug);
    },
    // The card takes the last slot of the second row on a two- or three-column grid
    conciergeCardIndex() {
      if (this.isMobile) {
        return 1;
      }

      return this.isThreeColumnGrid ? 5 : 3;
    },
    conciergeAreaPrefill() {
      return {
        area: this.filters.location.value,
        ...getBoundariesCenter(this.boundaries),
      };
    },
    conciergeBriefUrl() {
      const { date, timeFrom, timeTo, people } = this.filters;

      return withPrefill(this.concierge.briefUrl, {
        tag: this.eventTypeSlug,
        date: date.value ? window.dayjs(date.value, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
        start: HOURS_MAP_24[timeFrom.value],
        end: HOURS_MAP_24[timeTo.value],
        guests: people.value || null,
        ...this.conciergeAreaPrefill,
      });
    },
  },
  watch: {
    isMobile() {
      this.onMobileViewChanged();
    },
    mobileView: {
      immediate: true,
      handler() {
        this.onMobileViewChanged();
      },
    },
    rooms: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.setWidthVariables();
        });
      },
    },
    conciergeAreaPrefill: {
      immediate: true,
      handler: setAreaPrefill,
    },
    isChristmasBannerVisible: {
      immediate: true,
      handler(visible) {
        if (visible && !this.christmasBannerSeen) {
          this.christmasBannerSeen = true;
          this.trackChristmasBanner('visible');
        }
      },
    },
  },
  methods: {
    ...mapActions([
      'switchEventToChristmas',
    ]),
    onMobileViewChanged() {
      if (this.isMobile && this.mobileView === 'map') {
        document.body.classList.add('search-map-mobile-view');
      } else {
        document.body.classList.remove('search-map-mobile-view');
      }
    },
    paginationChange(page) {
      this.$store.commit('setPage', page);
      this.$store.dispatch('applyFilters', {
        changedFilter: 'Page',
        value: page,
      });
      this.$store.dispatch('scrollTop');
    },

    onGridColumnsChange(event) {
      this.isThreeColumnGrid = event.matches;
    },

    getRoomKey(room, i) {
      return `${i}-${JSON.stringify(room)}`;
    },

    trackChristmasBanner(interaction) {
      App.track.event('search_page_interaction', {
        section: 'list',
        element: 'christmas_banner',
        interaction,
      });
    },
    christmasBannerClick() {
      this.trackChristmasBanner('click');
      this.switchEventToChristmas();
    },

    // Room events
    beforeSlide(payload, room, index) {
      this.$store.dispatch('roomCarouselTrackArrowClick', {
        ...payload,
        room,
        index,
      });
    },
    trackClick(payload, room, index) {
      this.$store.dispatch('trackRoomClicked', {
        ...payload,
        room,
        index,
      });
    },
    mouseOver(room) {
      if (this.isMobile) {
        return;
      }
      this.$store.dispatch('highlightMarker', room.venue_id);
    },
    mouseOut(room) {
      if (this.isMobile) {
        return;
      }
      this.$store.dispatch('unHighlightMarker', room.venue_id);
    },
    setWidthVariables() {
      const width = this.$refs.searchResultsItems.offsetWidth;
      if (!width) {
        return;
      }
      this.$refs.searchResults.setAttribute('style', `--width-1-2: ${Math.round(width / 2)}px; --width-1-3: ${Math.round(width / 3)}px; --width-1-4: ${Math.round(width / 4)}px; --width-1-5: ${Math.round(width / 5)}px`);
    },
  },

  created() {
    window.inView.offset(-500);
    window.inView.test(App.es6.shared.in_view_test_function);

    if (isMobileFn()) {
      this.$store.dispatch('getRooms', { trigger: 'initMap' });
    }

    App.es6.shared.user_data.getUser().then((user) => {
      this.isGuest = user.type === 'guest';
    });
    $.subscribe('loginState:change', () => {
      this.isGuest = false;
    });
    threeColumnGridQuery.addEventListener('change', this.onGridColumnsChange);
  },
  mounted() {
    this.$nextTick(() => {
      this.setWidthVariables();
    });
    window.addEventListener('resize', throttle(this.setWidthVariables, 300));
  },
  beforeUnmount() {
    window.removeEventListener('resize', throttle(this.setWidthVariables, 300));
    threeColumnGridQuery.removeEventListener('change', this.onGridColumnsChange);
  },
};
</script>

<style lang="scss">
.v-search-results-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 16px;

  .lory-slider {
    box-shadow: -6px 0px 0px -5px #fff inset, 6px 0px 0px -5px #fff inset;
  }
}

.v-search-results-items--loading {
  opacity: 0.25;
  pointer-events: none;
}

@media (max-width: 700px) {
  .results-pagination, .results-pagination .pagination {
    padding-bottom: 0 !important;
  }
}
</style>
